<template>
  <v-container
    id="daily-flight-tables-view"
    fluid
    tag="section"
  >
    <material-card
      icon="$daily_flight"
      color="accent"
      help="daily-flight"
    >
      <template v-slot:title>
        <v-card-text
          v-if="!$vuetify.breakpoint.xs"
          class="text-h5 pb-0 pt-2"
        >
          <span class="px-2"> {{ page_title }} </span>
          <span class="px-2"> {{ current_date }} </span>
          <span class="px-2"> {{ current_time }} </span>
          <span class="px-2"> {{ profile.organization.base_airport.name }} {{ profile.organization.base_airport.icao }} </span>
          <br v-if="$vuetify.breakpoint.mobile">
          <span class="px-2">
            <v-icon> {{ '$sunset' }} </v-icon> {{ sun.sunset }}
          </span>
          <span v-if="checkModule('ogn')" class="green--text font-weight-bold">
            OGN
          </span>
        </v-card-text>
      </template>
      <template v-slot:title2>
        <tooltip-btn
          icon="$dashboard"
          tooltip="tooltip_dashboard"
          custom-class="px-5 ml-3"
          @click="dashboard"
        />
        <tooltip-btn
          icon="$logout"
          tooltip="tooltip_logout"
          @click="logout"
        />
      </template>

      <v-card-title class="py-0">
        <v-btn
          v-shortkey.once="['f2']"
          color="primary"
          :disabled="!editor"
          class="mb-2 px-2"
          :block="this.$vuetify.breakpoint.xs"
          @shortkey="newPreFlight()"
          @click="newPreFlight()"
        >
          {{ $t('new_item',{ item: $t('flight') }) }}
        </v-btn>
        <v-row class="ml-0 my-1">
          <daily-flight-plane-informations
            :date="operation_date"
          />
          <daily-flight-user-informations
            :date="operation_date"
          />
          <v-btn
            v-if="items.length == 0 && competitionModuleOn"
            class="mx-2 py-2"
            color="orange"
            @click="duplicateLastDay"
          >
            {{ $t('duplicate_last_day') }}
            <v-icon
              right
              dark
            >
              {{ '$duplicate' }}
            </v-icon>
          </v-btn>
          <v-btn
            v-if="faultReportModuleOn && !profile.has_role.student"
            color="orange"
            class="mx-2 py-2"
            @click="$router.push('/plane-fault/new').catch(() => {})"
          >
            {{ $t('plane_fault_report') }}
            <v-icon
              right
              dark
            >
              {{ '$plane_fault_reports' }}
            </v-icon>
          </v-btn>
        </v-row>
        <v-spacer />
        <date-field
          v-model="operation_date"
          field-name="date"
          :events="flight_dates"
          hide-details
          class-self="mr-1"
        />
        <v-text-field
          v-model="search"
          append-icon="$search"
          class="ml-auto"
          hide-details
          :label="$t('search_records')"
          single-line
          style="max-width: 400px;"
        />
      </v-card-title>

      <v-card-text class="daily-flight">
        <v-data-table
          ref="table"
          :headers="computed_headers"
          :items="items"
          :search="search"
          :loading="loading"
          :loading-text="$t('loading')"
          :footer-props="footerProps"
          :options.sync="options"
          :items-per-page="7"
          :item-class="flight_status"
          :custom-sort="customSort"
          sort-by="takeoff_time"
          sort-desc
        >
          <template #item.date="{item}">
            {{ item.date | dateFormat }}
          </template>

          <template #item.plane.reg_num="{item}">
            {{ item.plane.reg_num }} - {{ item.plane.model }}
          </template>

          <template #item.takeoff_time="{item}">
            <tooltip-btn
              :show="item.takeoff_time === null"
              :disabled="!editor"
              :text="false"
              fab
              color="primary"
              icon="$takeoff"
              tooltip="tooltip_takeoff"
              @click="takeoff(item)"
            />
            <span class="mx-1">{{ item.takeoff_time | timeFormat }} - {{ item.landing_time | timeFormat }}</span>
            <tooltip-btn
              :show="item.takeoff_time !== null && item.landing_time === null"
              :disabled="!editor"
              :text="false"
              fab
              color="primary"
              icon="$landing"
              tooltip="tooltip_landing"
              @click="landing(item)"
            />
            <span :style="item.landing_time ? 'color: green' : 'color: orange'">
              {{ timediff(item.takeoff_time, item.landing_time ? item.landing_time : current_time) }}
            </span>
          </template>

          <template #item.pic.name="{item}">
            <span
              class="d-inline-block text-truncate"
              style="max-width: 120px;"
            >
              {{ item.pic.name }}
            </span>
          </template>

          <template #item.sic.name="{item}">
            <span
              class="d-inline-block text-truncate"
              style="max-width: 120px;"
            >
              {{ item.sic ? item.sic.name : '' }}
            </span>
          </template>

          <template #item.takeoff_type="{item}">
            <template v-if="item.takeoff_type_id === 'tof_02'">
              <tow-plane-selector
                v-if="item.record_type !== 'flight' && editor"
                :pre-flight="item"
                :title="$t( 'types.'+item.takeoff_type.name) + (item.tow_plane ? (' ' + item.tow_plane.reg_num) : '')"
              />
              <span v-else>
                {{ $t( 'types.'+item.takeoff_type.name) + (item.tow_plane ? (' ' + item.tow_plane.reg_num) : '') }}
              </span>
              <span
                :style="item.tow_landing_time ? 'color: green' : 'color: orange'"
              >
                {{ (item.landing_time !==null && item.tow_landing_time === null) ? '?' : timediff(item.takeoff_time, item.tow_landing_time ? item.tow_landing_time : current_time) }}
              </span>
              <span v-if="item.tow_altitude">{{ item.tow_altitude }} m</span>
              <tooltip-btn
                :show="item.takeoff_time !== null && item.landing_time === null && item.tow_landing_time === null"
                :disabled="!editor"
                :text="false"
                fab
                color="primary"
                icon="$landing"
                tooltip="tooltip_tow_landing"
                @click="tow_landing(item)"
              />
            </template>
            <template v-else>
              {{ $t( 'types.'+item.takeoff_type.name) }}
            </template>
          </template>

          <template #item.task="{item}">
            {{ $t( 'types.'+item.task.name) }}
          </template>

          <template #item.actions="{item}">
            <v-btn
              v-if="item.plane.ogn_id !== null && item.takeoff_time !== null"
              icon
              color="blue"
              target="_blank"
              @click="ognURL(item)"
            >
              <v-icon v-html="'$map'" />
            </v-btn>
            <tooltip-btn
              v-if="item.record_type === 'flight' && editor"
              icon="$duplicate"
              tooltip="tooltip_duplicate"
              @click="duplicate(item)"
            />
            <v-tooltip
              v-if="item.transactions != null"
              bottom
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-if="checkModule('financial')"
                  class="px-0 ml-1"
                  elevation="0"
                  min-width="0"
                  small
                  text
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon v-html="'$transactions'" />
                </v-btn>
              </template>
              <pre
                v-for="transaction in item.transactions"
                :key="transaction.id"
              >{{ transCalc(transaction.calc) | pretty }}</pre>
            </v-tooltip>
            <tooltip-btn
              :disabled="!editor"
              color="edit"
              icon="$edit"
              tooltip="tooltip_edit"
              @click="edit(item.id, item.record_type)"
            />
          </template>
        </v-data-table>
      </v-card-text>
    </material-card>
  </v-container>
</template>

<script>
  // Utilities
  import { get, call, dispatch, sync } from 'vuex-pathify'
  import moment from 'moment'
  import { format } from 'date-fns'
  import Pusher from 'pusher-js'
  import DailyFlightPlaneInformations from '@/layouts/default/infos/DailyFlightPlaneInfo'
  import DailyFlightUserInformations from '@/layouts/default/infos/DailyFlightUserInfo'
  import TowPlaneSelector from '@/layouts/default/infos/TowPlaneSelector'
  import TooltipBtn from '@/components/TooltipBtn.vue'

  export default {
    name: 'FlightGlider',
    components: {
      TooltipBtn,
      TowPlaneSelector,
      DailyFlightUserInformations,
      DailyFlightPlaneInformations,
    },
    data () {
      return {
        items: [],
        // table
        footerProps: {
          itemsPerPageOptions: [5, 7, 10, 20, 25, 50],
        },
        search: '',
        interval: null,
        current_time: format(new Date(), 'HH:mm:ss'),
        current_date: format(new Date(), 'yyyy-MM-dd'),
        // flight_date: format(new Date(), 'yyyy-MM-dd'),
        flight_dates: [],
        sun: {},
        pusher: null,
      }
    },
    computed: {
      competitionModuleOn () { return this.profile.organization.modules.filter(m => m.name === 'competition').length > 0 },
      faultReportModuleOn () { return this.profile.organization.modules.filter(m => m.name === 'fault-report').length > 0 },
      headers () {
        return [
          {
            text: this.$t('fields.plane'),
            sortable: false,
            value: 'plane.reg_num',
          },
          {
            sortable: false,
            value: 'plane.tail_number',
            width: '1%',
            hide: !this.competitionModuleOn,
          },
          {
            text: this.$t('fields.time'),
            sortable: false,
            value: 'takeoff_time',
          },
          {
            hide_small: true,
            text: this.$t('fields.pic'),
            sortable: false,
            value: 'pic.name',
          },
          {
            hide_small: true,
            text: this.$t('fields.sic'),
            sortable: false,
            value: 'sic.name',
          },
          {
            hide_small: true,
            text: this.$t('fields.takeoff_type'),
            sortable: false,
            value: 'takeoff_type',
          },
          {
            value: 'actions',
            sortable: false,
            align: 'right',
          },
        ]
      },
      actions () {
        return [
          { color: 'edit', icon: '$edit', click: this.edit, disable: !this.editor },
        ]
      },
      loading: get('app/loading'),
      profile: get('auth/profile'),
      operation_date: sync('dailyFlight/operation_date'),
      options: sync('dailyFlight/options'),
      editor () { return this.profile.can['daily-flight-edit'] },
      page_title () { return this.$t('daily_flight') },
      computed_headers () {
        return this.headers.filter(h => h.hide_small !== this.$vuetify.breakpoint.sm && !h.hide)
      },
    },
    watch: {
      operation_date: {
        handler () {
          this.load()
        },
        deep: true,
      },
    },
    created () {
      this.interval = setInterval(() => {
        // if (this.items.length > 0) {
        this.current_time = format(new Date(), 'HH:mm:ss')
        // }
      }, 1000)

      this.fetchDatasource({ sourceType: 'flight-dates' }).then(response => {
        this.flight_dates = response.map(m => m.date) //  { date: '2021-15-15', events: ['2021-15-15'] }
      })

      this.fetchDatasource({ sourceType: 'sunset-sunrise' }).then(response => {
        this.sun = response
      }).catch(e => {
        // this.sun = { sunset: '?', sunrise: '?'}
        console.log(e)
      })
      this.load()
      // this.opera_date = format(new Date(), 'yyyy-MM-dd')

      // Pusher.logToConsole = true
      this.pusher = new Pusher('8f4461e65e0a77c1c6e0', {
        cluster: 'eu',
      })
      const channel = this.pusher.subscribe('ogn-channel')
      channel.bind('ogn-event', (data) => {
        console.log('OGN!!!')
        // console.log(data)
        this.load()
        // pusher.disconnect()
      })
    },
    destroyed () {
      this.pusher.disconnect()
    },
    methods: {
      getList: call('dailyFlight/search'),
      setTakeoff: call('dailyFlight/takeoff'),
      setLanding: call('dailyFlight/landing'),
      setTowLanding: call('dailyFlight/towLanding'),
      setDuplicate: call('dailyFlight/duplicate'),
      setDuplicateLastDay: call('dailyFlight/duplicateLastDay'),
      fetchDatasource: call('datasource/fetchDatasource'),
      getLatitudeLongitude: call('datasource/sunsetSunrise'),
      async load () {
        try {
          await this.getList({ date: this.operation_date }).then(response => {
            this.items = response
          }).catch(e => {
            console.log(e)
          })
        } catch (e) {
          console.log(e)
        }
      },
      edit (id, recordType) {
        const prefix = recordType === 'flight' ? 'f' : 'p'
        this.$router.push(`/daily-flight/${prefix}${id}`).catch(() => {})
      },
      newPreFlight () {
        this.$router.push('/daily-flight/new').catch(() => {})
      },
      async takeoff (item) {
        await this.setTakeoff({
          id: item.id,
          takeoff_time: format(new Date(), 'HH:mm'),
        }).then(response => {
          item.takeoff_time = response.takeoff_time
        }).catch(e => {
          console.log(e)
        })
      },
      async landing (item) {
        await this.setLanding({
          id: item.id,
          landing_time: format(new Date(), 'HH:mm'),
        }).then(response => {
          this.load()
          // item.landing_time = response.landing_time
        }).catch(e => {
          console.log(e)
        })
      },
      async tow_landing (item) {
        await this.setTowLanding({
          id: item.id,
          landing_time: format(new Date(), 'HH:mm'),
        }).then(response => {
          this.load()
          // item.landing_time = response.landing_time
        }).catch(e => {
          console.log(e)
        })
      },
      async duplicate (item) {
        item.competitionModuleOn = this.competitionModuleOn
        await this.setDuplicate(
          item,
        ).then(response => {
          this.load()
        }).catch(e => {
          dispatch('alert/setSnackbar', { text: Object.values(e)[0][0], type: 'error' })
        })
      },
      close_flight (item) {
        // item.landing_time = moment.now().format('HH:mm')
      },
      timediff (takeoffTime, landingTime) {
        if (takeoffTime === null) {
          return '00:00'
        }
        // console.log(landingTime)
        // ? item.landing_time : '18:03'
        return moment.utc(moment(landingTime, 'HH:mm:ss').diff(moment(takeoffTime, 'HH:mm:ss'))).format('HH:mm')
      },
      flight_status (item) {
        if (item.takeoff_time === null) {
          return 'yellow lighten-5'
        } else if (item.landing_time === null) {
          return 'blue lighten-5'
        }
      },
      customSort (items, index, isDesc) {
        items.sort((a, b) => {
          if (index[0] === 'takeoff_time') {
            if (b[index] === null) {
              console.log(`${a[index]} - ${b[index]}`)
              return 1
            } else if (b[index] === null && a[index] === null) {
              console.log(`${a[index]} - ${b[index]}`)
              return -1
            } else if (b.record_type !== a.record_type && a.record_type === 'flight') {
              return 1
            } else {
              return b[index].localeCompare(a[index])
            }
          }
        })
        return items
      },
      logout () {
        dispatch('auth/logout')
      },
      ognURL (item) {
        const takeoffTimestamp = moment(item.date + ' ' + item.takeoff_time, 'YYYY-MM-DD HH:mm:ss').unix()
        // console.log(item.date + ' ' + item.takeoff_time)

        const url = item.ogn_url ?? 'https://ogn.hu/map/index.html?flightday=' + item.date + '&aircraft=' + item.plane.ogn_id + '&takeoff_time=' + takeoffTimestamp + '&landing_time=' + moment().unix()

        window.open(url, '_blank')
      },
      dashboard () {
        this.$router.push('/').catch(() => {})
      },
      checkModule (payload) {
        return this.profile.organization.modules.filter(m => m.name === payload).length > 0
      },
      transCalc (calc) {
        const arrayFailed = Object.entries(JSON.parse(calc)).map((arr) => (
          this.$t('fields.' + arr[0], { currency: this.profile.organization.currency.name }) + ': ' + arr[1]
        ))
        return arrayFailed
      },
      customSearch (value, search, item) {
        return Object.values(item).some(v => v && v.toString().toLowerCase().includes(search))
      },
      async duplicateLastDay () {
        await this.setDuplicateLastDay({ date: this.operation_date }).then(response => {
          this.load()
        }).catch(e => {
          dispatch('alert/setSnackbar', { text: Object.values(e)[0][0], type: 'error' })
        })
      },
      changeTowPlane (item) {
        console.log(item.plane.reg_num)
      },
    },
  }
</script>

<style lang="sass">

  .daily-flight
    .v-data-table > .v-data-table__wrapper > table > tbody > tr > td
      font-size: 18px !important
      font-weight: bolder !important
    .v-data-table > .v-data-table__wrapper > table > thead > tr > th
      font-size: 20px !important
      font-weight: bold !important
</style>
